import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useTranslations } from '../../../hooks/use-translations';

export default function RetryBulkInductionDialog({ dialogOpen, okAction, cancelAction, selectedStatuses }) {
  const { t } = useTranslations();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!dialogOpen) {
      setError('');
      setLoading(false);
    }
  }, [dialogOpen]);

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await okAction();
    } catch (err) {
      setError(
        typeof err.response?.data === 'string'
          ? err.response.data
          : t('sites.administrative.bulkRetryFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={loading ? undefined : cancelAction}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon icon="mdi:restart-alert" width="24" height="24" color="warning" />
          <Typography variant="h6" component="span">
            {t('sites.administrative.bulkRetryConfirmTitle')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 1 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">{t('sites.administrative.bulkRetryConfirmWarning')}</Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('sites.administrative.bulkRetryConfirmStatuses')}
          </Typography>
          <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {(selectedStatuses || []).join(', ')}
          </Typography>
        </Box>
        {error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={cancelAction} disabled={loading} color="inherit">
          {t('dashboard.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !selectedStatuses?.length}
          variant="contained"
          color="warning"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {t('sites.administrative.bulkRetryConfirmAction')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RetryBulkInductionDialog.propTypes = {
  dialogOpen: PropTypes.bool.isRequired,
  okAction: PropTypes.func.isRequired,
  cancelAction: PropTypes.func.isRequired,
  selectedStatuses: PropTypes.arrayOf(PropTypes.string),
};
